/**
 * 批量任务轮询服务 Store
 * 
 * 职责：
 * - 全局单例轮询服务，使用引用计数管理轮询生命周期
 * - 自适应轮询间隔（根据任务状态动态调整）
 * - 支持指数退避和错误重试
 * - 支持页面可见性检测（后台标签页暂停轮询）
 * 
 * 设计原则：
 * - 组件只声明"需要轮询"（acquire）和"不再需要"（release）
 * - 轮询器自动管理启动/停止，组件无需关心细节
 * - 引用计数为 0 时自动停止轮询
 * 
 * @version 1.0.0
 * @author Frontend Team
 * @since 2025-11-13
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useBatchStore } from './batch'

/**
 * 轮询状态
 */
type PollingState = 'idle' | 'running' | 'paused' | 'error'

/**
 * 轮询间隔配置
 */
const POLLING_INTERVALS = {
  FAST: 2000,      // 快速：任务刚开始或接近完成
  NORMAL: 5000,    // 正常：任务进行中
  SLOW: 10000,     // 慢速：长时间运行的任务
  RETRY: 15000     // 重试：出错后的重试间隔
} as const

/**
 * 最大连续错误次数
 */
const MAX_ERROR_COUNT = 5

export const useBatchPollingStore = defineStore('batchPolling', () => {
  const batchStore = useBatchStore()

  // ============= 状态 =============
  
  /**
   * 引用计数：有多少个组件/场景需要轮询
   */
  const refCount = ref(0)

  /**
   * 轮询状态
   */
  const state = ref<PollingState>('idle')

  /**
   * 当前轮询定时器
   */
  let pollingTimer: number | null = null

  /**
   * 当前轮询间隔（毫秒）
   */
  const currentInterval = ref<number>(POLLING_INTERVALS.NORMAL)

  /**
   * 连续错误次数
   */
  const errorCount = ref(0)

  /**
   * 最后一次错误信息
   */
  const lastError = ref<string | null>(null)

  /**
   * 页面是否可见
   */
  const isPageVisible = ref(true)

  // ============= 计算属性 =============

  /**
   * 是否正在轮询
   */
  const isPolling = computed(() => state.value === 'running')

  /**
   * 是否应该轮询
   * 条件：引用计数 > 0 且页面可见
   */
  const shouldPoll = computed(() => refCount.value > 0 && isPageVisible.value)

  // ============= 核心方法 =============

  /**
   * 声明需要轮询（引用计数 +1）
   */
  const acquire = () => {
    refCount.value++
    console.log(`[PollingStore] acquire() -> refCount = ${refCount.value}`)

    // 如果之前没有轮询，现在启动
    if (refCount.value === 1 && isPageVisible.value) {
      startPolling()
    }
  }

  /**
   * 声明不再需要轮询（引用计数 -1）
   */
  const release = () => {
    if (refCount.value > 0) {
      refCount.value--
      console.log(`[PollingStore] release() -> refCount = ${refCount.value}`)

      // 如果引用计数归零，停止轮询
      if (refCount.value === 0) {
        stopPolling()
      }
    }
  }

  /**
   * 强制重置引用计数（用于异常情况）
   */
  const reset = () => {
    console.log('[PollingStore] reset() - 强制重置引用计数')
    refCount.value = 0
    stopPolling()
  }

  /**
   * 启动轮询
   */
  const startPolling = () => {
    if (pollingTimer !== null) {
      console.log('[PollingStore] 轮询已在运行中，跳过启动')
      return
    }

    console.log('[PollingStore] 启动全局轮询')
    state.value = 'running'
    errorCount.value = 0
    lastError.value = null
    currentInterval.value = POLLING_INTERVALS.NORMAL

    // 立即执行一次轮询
    pollOnce()
  }

  /**
   * 停止轮询
   */
  const stopPolling = () => {
    if (pollingTimer !== null) {
      console.log('[PollingStore] 停止全局轮询')
      window.clearTimeout(pollingTimer)
      pollingTimer = null
    }
    state.value = 'idle'
  }

  /**
   * 暂停轮询（页面不可见时）
   */
  const pausePolling = () => {
    if (state.value === 'running') {
      console.log('[PollingStore] 暂停轮询（页面不可见）')
      if (pollingTimer !== null) {
        window.clearTimeout(pollingTimer)
        pollingTimer = null
      }
      state.value = 'paused'
    }
  }

  /**
   * 恢复轮询（页面重新可见时）
   */
  const resumePolling = () => {
    if (state.value === 'paused' && shouldPoll.value) {
      console.log('[PollingStore] 恢复轮询（页面可见）')
      state.value = 'running'
      pollOnce()
    }
  }

  /**
   * 执行一次轮询
   */
  const pollOnce = async () => {
    try {
      // 获取所有进行中的会话状态
      await batchStore.fetchActiveSessionsStatus()

      // 重置错误计数
      if (errorCount.value > 0) {
        errorCount.value = 0
        lastError.value = null
      }

      // 计算下一次轮询间隔
      const nextInterval = calculateNextInterval()

      if (nextInterval === 0) {
        // 没有进行中的任务，停止轮询
        console.log('[PollingStore] 没有进行中的任务，停止轮询')
        stopPolling()
        return
      }

      // 更新当前间隔
      currentInterval.value = nextInterval

      // 调度下一次轮询
      pollingTimer = window.setTimeout(pollOnce, currentInterval.value)
    } catch (err: any) {
      console.error('[PollingStore] 轮询出错:', err)
      
      // 增加错误计数
      errorCount.value++
      lastError.value = err.message || '轮询失败'

      // 检查是否超过最大错误次数
      if (errorCount.value >= MAX_ERROR_COUNT) {
        console.error('[PollingStore] 连续错误次数过多，停止轮询')
        state.value = 'error'
        stopPolling()
        return
      }

      // 使用重试间隔继续轮询
      currentInterval.value = POLLING_INTERVALS.RETRY
      pollingTimer = window.setTimeout(pollOnce, currentInterval.value)
    }
  }

  /**
   * 计算下一次轮询间隔（自适应策略）
   * 
   * 策略：
   * - 有活跃任务：使用 NORMAL 间隔（5 秒）
   * - 无活跃任务：返回 0（停止轮询）
   */
  const calculateNextInterval = (): number => {
    const activeSessions = batchStore.activeSessions

    if (activeSessions.length === 0) {
      // 没有进行中的任务，停止轮询
      return 0
    }

    // 打印所有会话的进度
    const progressInfo = activeSessions
      .map(s => `${s.sessionUid.slice(-8)}: phase=${s.phase}, progress=${s.progress}%`)
      .join(', ')
    console.log(`[PollingStore] 有 ${activeSessions.length} 个活跃任务 [${progressInfo}]`)

    // 使用正常轮询间隔
    return POLLING_INTERVALS.NORMAL
  }

  // ============= 页面可见性监听 =============

  /**
   * 初始化页面可见性监听
   */
  const initVisibilityListener = () => {
    if (typeof document === 'undefined') return

    const handleVisibilityChange = () => {
      const visible = !document.hidden
      isPageVisible.value = visible

      if (visible) {
        // 页面可见，如果有引用计数且处于暂停状态，恢复轮询
        if (shouldPoll.value && state.value === 'paused') {
          resumePolling()
        }
      } else {
        // 页面不可见，暂停轮询
        if (state.value === 'running') {
          pausePolling()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // 返回清理函数
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }

  // 初始化可见性监听
  initVisibilityListener()

  // ============= 导出 =============

  return {
    // 状态
    refCount: computed(() => refCount.value),
    state: computed(() => state.value),
    isPolling,
    currentInterval: computed(() => currentInterval.value),
    errorCount: computed(() => errorCount.value),
    lastError: computed(() => lastError.value),

    // 方法
    acquire,
    release,
    reset,
    startPolling,
    stopPolling
  }
})

